import { Box } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import AgentChatPanel from 'components/AgentChat/AgentChatPanel'
import CreateVersionContract from 'components/CreateDeal/CreateVersionContract'
import { QuickChatDrawer } from 'components/QuickChat'
import GlobalBatchTasksSheet from 'components/Tasks/GlobalBatchTasksSheet'
import { DrawerProvider } from 'context/TaskDrawerContext/DrawerContext'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useUser } from './api/aws/useUser'
import { useActiveUser } from './api/slack/useActiveUser'
import FloatingNavBar from './components/AppSidebar/FloatingNavBar'
import CreateContact from './components/CreateContact/CreateContact'
import CreateOrganization from './components/CreateOrganization/CreateOrganization'
import CreativeIntakeForm from './components/Forms/CreativeIntakeForm'
import MarketingTaskForm from './components/Forms/MarketingTaskForm'
import CreateTask from './components/Tasks/CreateTask'
import { useLayoutContext } from './context/LayoutProvider/LayoutProvider'
import { ROLES } from 'config/roles.config'
import { useChangeView } from './utils/useChangeView'

const MainContent = styled(Box)({
  transition: 'all 0.2s ease',
  overflow: 'hidden',
  height: '100vh',
  marginTop: 0,
  flex: 1,
  minWidth: 0,
})

// ─── Resize divider ─────────────────────────────────────────────────

const MIN_SIDE_SIZE = 320
const MIN_BOTTOM_SIZE = 200
const MAX_RATIO = 0.5

const ResizeDivider = ({
  direction,
  onResize,
}: {
  direction: 'horizontal' | 'vertical'
  onResize: (delta: number) => void
}) => {
  const theme = useTheme()
  const [isHovered, setIsHovered] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const startRef = useRef<number>(0)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      setIsDragging(true)
      startRef.current = direction === 'horizontal' ? e.clientX : e.clientY

      const onMove = (ev: MouseEvent) => {
        const current = direction === 'horizontal' ? ev.clientX : ev.clientY
        const delta = current - startRef.current
        startRef.current = current
        onResize(delta)
      }
      const onUp = () => {
        setIsDragging(false)
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
      document.body.style.cursor = direction === 'horizontal' ? 'col-resize' : 'row-resize'
      document.body.style.userSelect = 'none'
    },
    [direction, onResize]
  )

  const isH = direction === 'horizontal'
  const active = isHovered || isDragging

  return (
    <Box
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        width: isH ? 5 : '100%',
        height: isH ? '100%' : 5,
        cursor: isH ? 'col-resize' : 'row-resize',
        position: 'relative',
        flexShrink: 0,
        zIndex: 10,
        '&::after': {
          content: '""',
          position: 'absolute',
          top: isH ? 0 : '50%',
          left: isH ? '50%' : 0,
          width: isH ? (active ? 3 : 1) : '100%',
          height: isH ? '100%' : (active ? 3 : 1),
          transform: isH ? 'translateX(-50%)' : 'translateY(-50%)',
          bgcolor: active ? 'primary.main' : 'divider',
          transition: 'all 0.15s ease',
        },
      }}
    />
  )
}

// ─── App Layout ─────────────────────────────────────────────────────

const AppLayout = ({
  children,
  isLoading,
  unreadCount,
}: {
  children: React.ReactNode
  isLoading: boolean
  unreadCount: number
}) => {
  const handleChangeView = useChangeView()
  const { activeUser } = useActiveUser()
  const { user: currentUser, isUserLoading: isCurrentUserLoading } = useUser(activeUser?.user?.id)
  const [isCreateOrganizationOpen, setIsCreateOrganizationOpen] = useState(false)
  const [isCreateDealOpen, setIsCreateDealOpen] = useState(false)
  const [isCreateContactOpen, setIsCreateContactOpen] = useState(false)
  const [isNotifyDrawerOpen, setIsNotifyDrawerOpen] = useState(false)
  const [isConnectedUsersOpen, setIsConnectedUsersOpen] = useState(false)
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false)
  const [isCreateBatchDigitalOpen, setisCreateBatchDigitalOpen] = useState(false)
  const [isCreativeIntakeOpen, setIsCreativeIntakeOpen] = useState(false)
  const [isMarketingFormOpen, setIsMarketingFormOpen] = useState(false)
  const devSectionIds = [...ROLES.ADMIN_IDS]
  // For route navigation tracking:
  const navigate = useNavigate()
  const location = useLocation()
  const selectedPath = location.pathname

  const { pinned, togglePinned, agentDock, agentDockSize, setAgentDockSize, isAgentDockOpen, setIsAgentDockOpen } =
    useLayoutContext()

  useEffect(() => {
    if (currentUser && !isCurrentUserLoading) {
      localStorage.setItem('userImage', currentUser?.image_512)
      localStorage.setItem('currentUser', JSON.stringify(currentUser))
    }
  }, [currentUser])

  const handleNavigation = useCallback(
    path => {
      const map = {
        '/nco': 'nco',
        '/projects': 'projects',
        '/mtb': 'mtb',
        '/deals': 'deals',
        '/organizations': 'organizations',
        '/contacts': 'contacts',
        '/teamtasks': 'teamtasks',
      }
      const newView = map[`/${path}`]
      if (newView) handleChangeView({ newView })
      else navigate(`/${path}`)
    },
    [handleChangeView, navigate]
  )

  const handleOpenCreateOrganization = useCallback(() => setIsCreateOrganizationOpen(true), [])
  const handleCloseCreateOrganization = useCallback(() => setIsCreateOrganizationOpen(false), [])

  const handleOpenCreateDeal = useCallback(() => setIsCreateDealOpen(true), [])
  const handleCloseCreateDeal = useCallback(() => setIsCreateDealOpen(false), [])

  const handleOpenCreateContact = useCallback(() => setIsCreateContactOpen(true), [])
  const handleCloseCreateContact = useCallback(() => setIsCreateContactOpen(false), [])

  const handleOpenNotifyDrawer = useCallback(() => setIsNotifyDrawerOpen(true), [])
  const handleCloseNotifyDrawer = useCallback(() => setIsNotifyDrawerOpen(false), [])

  const handleOpenConnectedUsers = useCallback(() => setIsConnectedUsersOpen(true), [])
  const handleCloseConnectedUsers = useCallback(() => setIsConnectedUsersOpen(false), [])

  const handleOpenCreateTask = useCallback(() => setIsCreateTaskOpen(true), [])
  const handleCloseCreateTask = useCallback(() => setIsCreateTaskOpen(false), [])

  const handleOpenCreativeIntake = useCallback(() => setIsCreativeIntakeOpen(true), [])
  const handleCloseCreativeIntake = useCallback(() => setIsCreativeIntakeOpen(false), [])

  const handleOpenMarketingForm = useCallback(() => setIsMarketingFormOpen(true), [])

  const handleCloseMarketingForm = (event, reason) => {
    if (reason && reason === 'backdropClick') return
    setIsMarketingFormOpen(false)
  }
  const handleOpenBatchDigital = () => setisCreateBatchDigitalOpen(true)
  const handleCloseBatchDigital = () => setisCreateBatchDigitalOpen(false)
  const navigationHandlers = useMemo(
    () => ({
      handleOpenCreateOrganization: handleOpenCreateOrganization,
      handleOpenCreateDeal: handleOpenCreateDeal,
      handleOpenCreateContact: handleOpenCreateContact,
      onOpenCreateTask: handleOpenCreateTask,
      onCloseCreateTask: handleCloseCreateTask,
      onOpenCreateBatchDigital: handleOpenBatchDigital,
      onCloseCreateBatchDigital: handleCloseBatchDigital,
      onOpenCreativeIntake: handleOpenCreativeIntake,
      onCloseCreativeIntake: handleCloseCreativeIntake,
      onOpenMarketingForm: handleOpenMarketingForm,
      onCloseMarketingForm: handleCloseMarketingForm,
      isConnectedUsersOpen: isConnectedUsersOpen,
      handleOpenConnectedUsers: handleOpenConnectedUsers,
      handleCloseConnectedUsers: handleCloseConnectedUsers,
      isNotifyDrawerOpen: isNotifyDrawerOpen,
      onOpenNotify: handleOpenNotifyDrawer,
      onCloseNotify: handleCloseNotifyDrawer,
      devSectionIds: devSectionIds,
      handleNavigation: handleNavigation,
    }),
    [
      handleOpenCreateOrganization,
      handleOpenCreateDeal,
      handleOpenCreateContact,
      handleOpenCreateTask,
      handleCloseCreateTask,
      handleOpenBatchDigital,
      handleCloseBatchDigital,
      handleOpenCreativeIntake,
      handleCloseCreativeIntake,
      handleOpenMarketingForm,
      handleCloseMarketingForm,
      isConnectedUsersOpen,
      handleOpenConnectedUsers,
      handleCloseConnectedUsers,
      isNotifyDrawerOpen,
      handleOpenNotifyDrawer,
      handleCloseNotifyDrawer,
      devSectionIds,
      handleNavigation,
    ]
  )

  // ─── Dock resize handler ────────────────────────────────────────

  const handleDockResize = useCallback(
    (delta: number) => {
      setAgentDockSize(prev => {
        const isBottom = agentDock === 'bottom'
        const maxSize = isBottom ? window.innerHeight * MAX_RATIO : window.innerWidth * MAX_RATIO
        const minSize = isBottom ? MIN_BOTTOM_SIZE : MIN_SIDE_SIZE
        // For left/bottom, positive delta = larger; for right, positive delta = smaller
        const adjusted = agentDock === 'right' ? prev - delta : prev + delta
        return Math.max(minSize, Math.min(maxSize, adjusted))
      })
    },
    [agentDock, setAgentDockSize]
  )

  // ─── Docked agent chat panel ────────────────────────────────────

  const isBottom = agentDock === 'bottom'
  const showDocked = isAgentDockOpen && agentDock

  const dockedPanel = showDocked && (
    <Box
      sx={{
        width: isBottom ? '100%' : agentDockSize,
        height: isBottom ? agentDockSize : '100%',
        minWidth: isBottom ? undefined : MIN_SIDE_SIZE,
        minHeight: isBottom ? MIN_BOTTOM_SIZE : undefined,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderLeft: agentDock === 'right' ? '1px solid' : undefined,
        borderRight: agentDock === 'left' ? '1px solid' : undefined,
        borderTop: isBottom ? '1px solid' : undefined,
        borderColor: 'divider',
      }}
    >
      <AgentChatPanel
        open={true}
        onClose={() => setIsAgentDockOpen(false)}
        mode="docked"
      />
    </Box>
  )

  const resizeDivider = showDocked && (
    <ResizeDivider
      direction={isBottom ? 'vertical' : 'horizontal'}
      onResize={handleDockResize}
    />
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <QuickChatDrawer />

      <Box
        sx={{
          display: 'flex',
          flexDirection: isBottom ? 'column' : 'row',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {agentDock === 'left' && dockedPanel}
        {agentDock === 'left' && resizeDivider}

        <MainContent>{children}</MainContent>

        {agentDock === 'right' && resizeDivider}
        {agentDock === 'right' && dockedPanel}

        {agentDock === 'bottom' && resizeDivider}
        {agentDock === 'bottom' && dockedPanel}
      </Box>

      {isCreateOrganizationOpen && (
        <CreateOrganization open={isCreateOrganizationOpen} onClose={handleCloseCreateOrganization} />
      )}
      {isCreateDealOpen && <CreateVersionContract open={isCreateDealOpen} onClose={handleCloseCreateDeal} />}
      {isCreateContactOpen && <CreateContact open={isCreateContactOpen} onClose={handleCloseCreateContact} />}
      {isCreateTaskOpen && <CreateTask open={isCreateTaskOpen} onClose={handleCloseCreateTask} />}
      {isCreativeIntakeOpen && <CreativeIntakeForm open={isCreativeIntakeOpen} onClose={handleCloseCreativeIntake} />}
      {isMarketingFormOpen && <MarketingTaskForm open={isMarketingFormOpen} onClose={handleCloseMarketingForm} />}
      {isCreateBatchDigitalOpen && (
        <DrawerProvider>
          <GlobalBatchTasksSheet open={isCreateBatchDigitalOpen} onClose={handleCloseBatchDigital} />
        </DrawerProvider>
      )}
      <FloatingNavBar
        {...navigationHandlers}
        selectedPath={selectedPath}
        imageUrl={activeUser?.user?.profile?.image_512 || null}
        user={currentUser}
        unreadCount={unreadCount}
        pinned={pinned}
        onTogglePinned={togglePinned}
        isLoading={isLoading}
      />
    </LocalizationProvider>
  )
}

export default AppLayout
